import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormFeedback } from '@/components/ui/form-feedback';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-clinical-study';
import { formatDate } from '@/lib/intl';
import { usePendingWrite } from '@/lib/use-pending-write';
import { useTreatmentDatePoolPicker } from './useTreatmentDatePoolPicker';

type StudySubject =
  paths['/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/treatment-date-reservation']['post']['responses'][200]['content']['application/json'];

const reservationSchema = z.object({
  treatmentDatePoolEntryId: z.string().uuid('Deve essere un UUID valido'),
});
type ReservationForm = z.infer<typeof reservationSchema>;

export function TreatmentDateReservationSection({
  studyId,
  subjectId,
  onReserved,
}: {
  studyId: string;
  subjectId: string;
  onReserved: (subject: StudySubject) => void;
}) {
  const { t } = useTranslation('clinical-study');
  const [apiError, setApiError] = useState<string | null>(null);
  const pool = useTreatmentDatePoolPicker();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ReservationForm>({ resolver: zodResolver(reservationSchema) });
  const { pending, run } = usePendingWrite();

  const onSubmit = async (values: ReservationForm) => {
    setApiError(null);
    const { data, error, response } = await apiClient.POST(
      '/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/treatment-date-reservation',
      {
        params: { path: { studyId, subjectId } },
        body: { treatmentDatePoolEntryId: values.treatmentDatePoolEntryId },
      },
    );
    if (error !== undefined || !response.ok || data === undefined) {
      setApiError(error?.detail ?? t('reservation.genericError'));
      return;
    }
    reset();
    onReserved(data);
    toast.success(t('reservation.reservedToast'));
  };

  const remainingEntries =
    pool.status === 'ready'
      ? pool.entries.filter((entry) => entry.capacity - entry.consumedCount > 0)
      : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('reservation.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {apiError && <FormFeedback tone="error">{apiError}</FormFeedback>}
        <form
          className="space-y-4"
          onSubmit={(event) => void handleSubmit((values) => run(() => onSubmit(values)))(event)}
        >
          {pool.status === 'loading' && (
            <p className="text-sm text-muted-foreground">{t('reservation.poolLoading')}</p>
          )}
          {/* SL-0045 Activity 9: 'forbidden' shares the refusal presentation. The state stays in the
              union because a 403 remains reachable for any role holding neither pool code, and it
              must surface as a refusal — never as the raw-ID field this activity deleted. */}
          {(pool.status === 'error' || pool.status === 'forbidden') && (
            <div className="space-y-2">
              <FormFeedback tone="error">{t('reservation.poolLoadError')}</FormFeedback>
              <Button type="button" variant="outline" size="sm" onClick={pool.refetch}>
                {t('reservation.poolRetry')}
              </Button>
            </div>
          )}
          {pool.status === 'ready' && (
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="reservation-pool-entry">
                {t('reservation.poolEntryLabel')}
              </label>
              <Controller
                control={control}
                name="treatmentDatePoolEntryId"
                render={({ field }) => (
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger id="reservation-pool-entry">
                      <SelectValue placeholder={t('reservation.poolEntryPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {remainingEntries.map((entry) => (
                        <SelectItem key={entry.id} value={entry.id}>
                          {formatDate(entry.date)} —{' '}
                          {t('reservation.remainingSlots', {
                            count: entry.capacity - entry.consumedCount,
                          })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {remainingEntries.length === 0 && (
                <p className="text-sm text-muted-foreground">{t('reservation.poolEmptyMessage')}</p>
              )}
            </div>
          )}
          {errors.treatmentDatePoolEntryId && (
            <FormFeedback tone="error" id="reservation-pool-entry-id-error">
              {errors.treatmentDatePoolEntryId.message}
            </FormFeedback>
          )}
          {/* The `pending` term is SL-0046's in-flight submit guard. Dropping it re-admits the
              double-submit defect the 2026-08-04 incident measured at 1.3 ms and 65 ms apart;
              SL-0045 Activity 9 reduced the pool test to `!== 'ready'` when the
              'forbidden' fallback was deleted, and carried the term through. */}
          <Button type="submit" disabled={pool.status !== 'ready' || pending}>
            {t('reservation.submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
